import React, { useState } from 'react';
import { FaClipboard, FaCheck, FaGoogle } from 'react-icons/fa';
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import Section from './components/Section';
import translationsData from './data/translations.json';
import emailContent from './data/emailContent.json';

function App() {
    const [language, setLanguage] = useState('en');
    const [copied, setCopied] = useState(false);
    const translations = translationsData[language];

    const scrollToSection = (sectionId) => {
        const element = document.getElementById(sectionId);
        if (element) {
            const offset = 100;
            const elementPosition = element.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.pageYOffset - offset;

            window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth'
            });
        }
    };

    const sendEmail = () => {
        const emailData = emailContent.en;
        const subject = encodeURIComponent(emailData.subject);
        const body = encodeURIComponent(emailData.body);

        let mailtoLink = `mailto:questions@valvesoftware.com?subject=${subject}&body=${body}`;

        if (emailData.cc && emailData.cc.length > 0) {
            const ccString = emailData.cc.join(',');
            mailtoLink += `&cc=${encodeURIComponent(ccString)}`;
        }

        window.location.href = mailtoLink;
    };

    const copyEmailToClipboard = () => {
        const emailData = emailContent.en;
        const emailText = `Subject: ${emailData.subject}\n\n${emailData.body}`;

        navigator.clipboard.writeText(emailText)
            .then(() => {
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
            })
            .catch(err => {
                console.error('Failed to copy: ', err);
                const textArea = document.createElement('textarea');
                textArea.value = emailText;
                document.body.appendChild(textArea);
                textArea.select();
                document.execCommand('copy');
                document.body.removeChild(textArea);
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
            });
    };

    const openInGmail = () => {
        const emailData = emailContent.en;

        // Gmail URL paraméterei
        const to = 'questions@valvesoftware.com';
        const subject = encodeURIComponent(emailData.subject);
        const body = encodeURIComponent(emailData.body);

        // Gmail compose URL összeállítása
        let gmailUrl = `https://mail.google.com/mail/?view=cm&fs=1&to=${to}&su=${subject}&body=${body}`;

        // CC címek hozzáadása, ha vannak
        if (emailData.cc && emailData.cc.length > 0) {
            const ccString = emailData.cc.join(',');
            gmailUrl += `&cc=${encodeURIComponent(ccString)}`;
        }

        // Gmail megnyitása új lapon
        window.open(gmailUrl, '_blank', 'noopener,noreferrer');
    }

    const renderEmailRecipients = () => {
        const emailData = emailContent.en;

        return (
            <div className="text-gray-500 text-sm pt-8">
                <p className="mb-2">
                    {translations.action.email_language}
                </p>
                <div className="text-neon-red">
                    <div className="font-mono">
                        {emailData.cc && emailData.cc.length > 0
                            ? `questions@valvesoftware.com, ${emailData.cc.join(', ')}`
                            : 'questions@valvesoftware.com'
                        }
                    </div>
                    {emailData.cc && emailData.cc.length > 0 && (
                        <p className="text-gray-400 text-xs mt-2">
                            ({translations.action.includes} {emailData.cc.length} CC {translations.action.recipients})
                        </p>
                    )}
                </div>
            </div>
        );
    };

    return (
        <div className="min-h-screen">
            <Navbar
                currentLanguage={language}
                onLanguageChange={setLanguage}
                scrollToSection={scrollToSection}
                translations={translations.nav}
            />

            <Hero translations={translations} />

            <div className="h-16 bg-gradient-to-b from-transparent to-dark-bg"></div>

            <Section
                id="problems"
                title={translations.problems.title}
                items={translations.problems.items}
                theme="dark"
            />

            <div className="h-20 bg-gradient-to-b from-gray-900/50 to-black/50"></div>

            <Section
                id="valve-neglect"
                title={translations.valve_neglect.title}
                items={translations.valve_neglect.items}
                theme="darker"
            />

            <div className="h-20 bg-gradient-to-b from-black/50 to-gray-900/50"></div>

            <Section
                id="fake-solutions"
                title={translations.fake_solutions.title}
                items={translations.fake_solutions.items}
                theme="dark"
            />

            <div className="h-20 bg-gradient-to-b from-gray-900/50 to-black/50"></div>

            <Section
                id="demands"
                title={translations.demands.title}
                items={translations.demands.items}
                theme="darker"
            />

            <div className="h-24 bg-gradient-to-b from-dark-bg to-black"></div>

            <section id="action" className="relative py-24 bg-gradient-to-b from-black to-dark-bg overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-dark-bg/30"></div>

                <div className="relative z-10">
                    <div className="section-container">
                        <div className="max-w-4xl mx-auto text-center">
                            <h2 className="text-4xl md:text-5xl font-bold mb-12 heading-gradient pb-4">
                                {translations.action.title}
                            </h2>

                            <p className="text-xl text-gray-300 mb-16 leading-relaxed">
                                {translations.action.description}
                            </p>

                            <div className="bg-dark-card rounded-2xl p-10 border-2 border-neon-red neon-glow max-w-2xl mx-auto mb-16">
                                <h3 className="text-2xl font-bold text-neon-red mb-6">
                                    {translations.action.email_button}
                                </h3>
                                <p className="text-gray-400 mb-8">
                                    {translations.action.email_hint}
                                </p>

                                {/* Első sor: Email gomb és Másolás gomb */}
                                <div className="flex gap-4 mb-4">
                                    <button
                                        onClick={sendEmail}
                                        className="flex-1 py-5 bg-neon-red text-black font-bold rounded-lg text-xl hover:bg-neon-red-dark transition-colors flex items-center justify-center space-x-3 neon-glow"
                                    >
                                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                        </svg>
                                        <span>{translations.action.email_button}</span>
                                    </button>

                                    <button
                                        onClick={copyEmailToClipboard}
                                        className="w-16 py-5 bg-dark-card border-2 border-neon-red text-neon-red font-bold rounded-lg hover:bg-neon-red/10 transition-colors flex items-center justify-center relative group"
                                        title={copied ? translations.action.copied_tooltip : translations.action.copy_tooltip}
                                    >
                                        {copied ? (
                                            <FaCheck className="w-6 h-6 text-green-500" />
                                        ) : (
                                            <FaClipboard className="w-6 h-6" />
                                        )}

                                        <div className="absolute -top-12 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-sm py-2 px-3 rounded-md opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap">
                                            {copied ? translations.action.copied_tooltip : translations.action.copy_tooltip}
                                            <div className="absolute bottom-[-6px] left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-gray-900"></div>
                                        </div>
                                    </button>
                                </div>

                                {/* Második sor: Gmail gomb - hasonló stílus */}
                                <div className="flex gap-4">
                                    <button
                                        onClick={openInGmail}
                                        className="flex-1 py-5 bg-neon-red text-black font-bold rounded-lg text-xl hover:bg-neon-red-dark transition-colors flex items-center justify-center space-x-3 neon-glow"
                                        title={translations.action.gmail_hint}
                                    >
                                        <FaGoogle className="w-6 h-6" />
                                        <span>{translations.action.gmail_button}</span>
                                    </button>
                                </div>

                                {/* Gmail hint */}
                                <p className="text-gray-500 text-sm mt-4 mb-4">
                                    {translations.action.gmail_hint}
                                </p>

                                {copied && (
                                    <div className="mt-4 p-3 bg-green-900/30 border border-green-500 rounded-lg text-green-400 text-sm">
                                        {translations.action.copied_message}
                                    </div>
                                )}
                            </div>

                            {renderEmailRecipients()}
                        </div>
                    </div>
                </div>
            </section>

            <footer className="py-12 border-t border-gray-900 mt-24">
                <div className="section-container text-center text-gray-500">
                    <p className="mb-4">© {new Date().getFullYear()} fixcs2now.com - Not affiliated with Valve Corporation</p>
                    <p className="text-sm">Counter-Strike is a registered trademark of Valve Corporation</p>
                </div>
            </footer>
        </div>
    );
}

export default App;